import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
  lineElementClasses,
  markElementClasses,
  ChartsGrid,
  CurveType,
  LineChartPro,
  LinePlot,
  MarkPlot,
  ChartsXAxis,
} from "@mui/x-charts-pro";
import { CustomItemTooltipPercentage } from "../../../../../../ResuableChartTooltip/CustomItemTooltipPercentage";
import { useSelector } from "react-redux";
import { useMemo, useState, useCallback } from "react";
import { Autocomplete, TextField, Chip } from "@mui/material";

// Time period options for autocomplete
const TIME_PERIOD_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

// Special color for "Optival" (You)
const OPTIVAL_COLOR = "#9b60f3";

// Color palette for other competitors (maximum 5 others + Optival = 6 total)
const COMPETITOR_COLORS = [
  "#FF928A",
  "#3CC3DF",
  "#FFAE4C",
  "#FF4CDB",
  "#3CDF72",
];

interface CompetitorData {
  display_url_domain: string;
  activity_date: string;
  top_of_page_rate: number;
  search_impression_share: number;
  position_above_rate: number;
}

// Data sampling function for large datasets
const sampleData = (data: any[], maxPoints: number = 100): any[] => {
  if (data.length <= maxPoints) return data;

  const step = Math.floor(data.length / maxPoints);
  const sampled = [];

  // Always include first and last points
  sampled.push(data[0]);

  for (let i = step; i < data.length - step; i += step) {
    sampled.push(data[i]);
  }

  sampled.push(data[data.length - 1]);
  return sampled;
};

// Data aggregation function for better performance
const aggregateDataByPeriod = (
  data: CompetitorData[],
  period: "daily" | "weekly" | "monthly" = "daily"
) => {
  if (period === "daily") return data;

  const groupedData = new Map<string, Map<string, CompetitorData[]>>();

  data.forEach((item) => {
    const date = new Date(item.activity_date);
    let periodKey: string;

    if (period === "weekly") {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      periodKey = weekStart.toISOString().split("T")[0];
    } else {
      // monthly
      periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-01`;
    }

    if (!groupedData.has(item.display_url_domain)) {
      groupedData.set(item.display_url_domain, new Map());
    }

    if (!groupedData.get(item.display_url_domain)!.has(periodKey)) {
      groupedData.get(item.display_url_domain)!.set(periodKey, []);
    }

    groupedData.get(item.display_url_domain)!.get(periodKey)!.push(item);
  });

  // Average the values for each period
  const aggregated: CompetitorData[] = [];

  groupedData.forEach((periods, domain) => {
    periods.forEach((items, periodKey) => {
      const avgShare =
        items.reduce((sum, item) => sum + item.search_impression_share, 0) /
        items.length;
      const avgTopRate =
        items.reduce((sum, item) => sum + item.top_of_page_rate, 0) /
        items.length;
      const avgPosRate =
        items.reduce((sum, item) => sum + item.position_above_rate, 0) /
        items.length;

      aggregated.push({
        display_url_domain: domain,
        activity_date: periodKey,
        search_impression_share: avgShare,
        top_of_page_rate: avgTopRate,
        position_above_rate: avgPosRate,
      });
    });
  });

  return aggregated;
};

const SearchImpressionShare = () => {
  const { competitorMetricsAccordionDetails } = useSelector(
    (state: any) => state.landingPageExperience
  );

  // State for competitor selection and time period
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(
    new Set()
  ) as any;
  const [aggregationPeriod, setAggregationPeriod] = useState<
    "daily" | "weekly" | "monthly"
  >("daily");

  // Memoized data processing with heavy optimizations
  const { chartData, seriesConfig, isEmpty, allCompetitors } = useMemo(() => {
    if (
      !competitorMetricsAccordionDetails ||
      competitorMetricsAccordionDetails.length === 0
    ) {
      return {
        chartData: [],
        seriesConfig: [],
        isEmpty: true,
        allCompetitors: [],
      };
    }

    // Step 1: Get all unique competitors and transform "You" to "Optival"
    const rawCompetitors = Array.from(
      new Set(
        competitorMetricsAccordionDetails.map(
          (item: CompetitorData) => item.display_url_domain
        )
      )
    );

    const allCompetitors = rawCompetitors.map((domain: any) =>
      domain.toLowerCase() === "you" ? "Optival" : domain
    );

    // Step 2: Auto-select competitors if none selected
    if (selectedCompetitors.size === 0) {
      const competitorAverages = allCompetitors.map((domain) => {
        // Map back to original domain for data filtering
        const originalDomain = domain === "Optival" ? "You" : domain;
        const domainData = competitorMetricsAccordionDetails.filter(
          (item: CompetitorData) =>
            item.display_url_domain.toLowerCase() ===
            originalDomain.toLowerCase()
        );
        const avgShare =
          domainData.reduce(
            (sum: number, item: CompetitorData) =>
              sum + item.search_impression_share,
            0
          ) / domainData.length;
        return { domain, avgShare };
      });

      // Always include "Optival" if it exists
      const optivalCompetitor = allCompetitors.find(
        (domain) => domain === "Optival"
      );
      const otherCompetitors = competitorAverages
        .filter((item) => item.domain !== "Optival")
        .sort((a, b) => b.avgShare - a.avgShare)
        .slice(0, optivalCompetitor ? 4 : 5) // Take 4 others if "Optival" exists, otherwise 5
        .map((item) => item.domain);

      const initialSelection = optivalCompetitor
        ? ["Optival", ...otherCompetitors]
        : otherCompetitors;

      setSelectedCompetitors(new Set(initialSelection));
      return {
        chartData: [],
        seriesConfig: [],
        isEmpty: false,
        allCompetitors,
      };
    }

    // Step 3: Filter data by selected competitors (map back to original domain names)
    const filteredData = competitorMetricsAccordionDetails.filter(
      (item: CompetitorData) => {
        const displayName =
          item.display_url_domain.toLowerCase() === "you"
            ? "Optival"
            : item.display_url_domain;
        return selectedCompetitors.has(displayName);
      }
    );

    if (filteredData.length === 0) {
      return { chartData: [], seriesConfig: [], isEmpty: true, allCompetitors };
    }

    // Step 4: Aggregate data based on period
    const aggregatedData = aggregateDataByPeriod(
      filteredData,
      aggregationPeriod
    );

    // Step 5: Group by competitor and create chart data (use display names)
    const competitorMap = new Map<string, Map<string, number>>();
    const dateSet = new Set<string>();

    aggregatedData.forEach((item) => {
      const displayName =
        item.display_url_domain.toLowerCase() === "you"
          ? "Optival"
          : item.display_url_domain;
      if (!competitorMap.has(displayName)) {
        competitorMap.set(displayName, new Map());
      }
      competitorMap
        .get(displayName)!
        .set(item.activity_date, item.search_impression_share);
      dateSet.add(item.activity_date);
    });

    // Step 6: Sort dates and create chart data
    const sortedDates = Array.from(dateSet).sort();

    // Step 7: Sample data if too many points
    const sampledDates = sampleData(sortedDates, 50);

    const chartData = sampledDates.map((date) => {
      const d = new Date(date);
      //  format to dd-mm-yyyy
      const formattedDate = d.toLocaleDateString("en-GB").replace(/\//g, "-");

      const dataPoint: any = { date: formattedDate };
      competitorMap.forEach((dateMap, domain) => {
        dataPoint[domain] = dateMap.get(date) || 0;
      });
      return dataPoint;
    });

    // Step 8: Create optimized series configuration with special handling for "Optival"
    const selectedCompetitorsList = Array.from(selectedCompetitors);

    // Sort competitors to put "Optival" first
    const sortedCompetitors = selectedCompetitorsList.sort((a: any, b) => {
      if (a === "Optival") return -1;
      if (b === "Optival") return 1;
      return a.localeCompare(b);
    });

    const seriesConfig = sortedCompetitors.map((domain: any, index) => {
      // Use special color for "Optival", otherwise use regular palette
      const color =
        domain === "Optival"
          ? OPTIVAL_COLOR
          : COMPETITOR_COLORS[index % COMPETITOR_COLORS.length];

      return {
        type: "line" as const,
        label: domain,
        data: chartData.map((point) => point[domain] || 0),
        color: color,
        curve: "linear" as CurveType,
      };
    });

    return { chartData, seriesConfig, isEmpty: false, allCompetitors };
  }, [
    competitorMetricsAccordionDetails,
    selectedCompetitors,
    aggregationPeriod,
  ]);

  // Optimized chart props
  const chartProps = useMemo(
    () => ({
      height: 300,
      series: seriesConfig,
      // Disable animations for better performance
      disableAnimations: true,
    }),
    [seriesConfig]
  );

  // Handle autocomplete competitor selection
  const handleCompetitorAutocompleteChange = useCallback(
    (_: any, newValue: string[]) => {
      // Always include Optival if it exists in allCompetitors
      const optivalExists = allCompetitors.includes("Optival");
      const finalSelection = optivalExists
        ? Array.from(new Set(["Optival", ...newValue])).slice(0, 6)
        : newValue.slice(0, 6);

      setSelectedCompetitors(new Set(finalSelection));
    },
    [allCompetitors]
  );

  if (isEmpty) {
    return (
      <>
        <Typography fontSize={18}>Search Impression Share</Typography>
        <Typography sx={{ color: "#00000099", mb: 2 }}>
          % of eligible impressions received
        </Typography>
        <Box
          sx={{
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ccc",
            borderRadius: 1,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography color="text.secondary">
            No auction data available for this keyword
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography fontSize={18}>Search Impression Share</Typography>
      <Typography sx={{ color: "#00000099", mb: 1 }}>
        % of eligible impressions received
      </Typography>

      {/* Time Period and Competitor Selection Controls */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        {/* Competitor Selection Autocomplete */}
        <Autocomplete<string, true, false, false>
          multiple
          size="small"
          color="secondary"
          value={Array.from(selectedCompetitors)}
          onChange={handleCompetitorAutocompleteChange}
          options={allCompetitors.filter((domain) => domain !== "Optival")}
          getOptionLabel={(option) => option}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagProps = getTagProps({ index });

              return (
                <Chip
                  variant="outlined"
                  label={option}
                  // ⬇️ Only spread delete handler if NOT Optival
                  {...(option === "Optival" ? {} : tagProps)}
                  sx={{
                    backgroundColor:
                      option === "Optival"
                        ? `${OPTIVAL_COLOR}20`
                        : "transparent",
                    borderColor:
                      option === "Optival" ? OPTIVAL_COLOR : undefined,
                  }}
                  size="small"
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Competitors"
              placeholder={
                selectedCompetitors.size >= 6
                  ? "Maximum 5 competitors"
                  : "Add competitors..."
              }
              color="secondary"
            />
          )}
          sx={{ minWidth: 300, flexGrow: 1 }}
          limitTags={3}
          ChipProps={{ size: "small" }}
          disabled={
            selectedCompetitors.size >= 6 &&
            !Array.from(selectedCompetitors).some((comp) =>
              allCompetitors
                .filter((domain) => domain !== "Optival")
                .includes(comp)
            )
          }
        />
        {/* Time Period Autocomplete */}
        <Autocomplete
          size="small"
          value={
            TIME_PERIOD_OPTIONS.find(
              (option) => option.value === aggregationPeriod
            ) || TIME_PERIOD_OPTIONS[0]
          }
          onChange={(_, newValue) => {
            if (newValue) {
              setAggregationPeriod(
                newValue.value as "daily" | "weekly" | "monthly"
              );
            }
          }}
          options={TIME_PERIOD_OPTIONS}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField {...params} label="Time Period" color="secondary" />
          )}
          sx={{ minWidth: 150 }}
          disableClearable
        />
      </Stack>

      <Stack sx={{ width: "100%", mb: "16px" }}>
        <Box sx={{ width: "100%" }}>
          <LineChartPro
            {...chartProps}
            xAxis={[
              {
                scaleType: "band",
                id: "my-x-axis",
                data: chartData.map((point) => point.date),
                disableLine: true,
                disableTicks: false,
              },
            ]}
            yAxis={[
              {
                id: "y-axis",
                scaleType: "linear",
                position: "right",
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: 100,
                valueFormatter: (value) => `${value}%`,
              },
            ]}
            margin={{ left: 20, right: 35, top: 25, bottom: 65 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 1.5, // Reduced for performance
                "&:hover": {
                  strokeWidth: "2.5",
                },
              },
              [`& .MuiChartsAxis-tickLabel`]: {
                fill: "grey !important",
              },
              [`& .MuiChartsAxis-tick`]: {
                stroke: "grey !important",
              },
              [`& .MuiChartsLegend-root text`]: {
                fill: "grey !important",
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.4", // Smaller marks for performance
                strokeWidth: 1,
                "&:hover": {
                  scale: "0.8",
                  strokeWidth: "1.5",
                },
              },
            }}
            slotProps={{
              legend: {
                direction: "row",
                position: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                labelStyle: {
                  fontSize: 11,
                },
                itemMarkWidth: 10,
                itemMarkHeight: 2,
                markGap: 6,
                itemGap: 8,
                padding: 8,
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltipPercentage />
            <LinePlot />
            <MarkPlot />
            <ChartsXAxis axisId="my-x-axis" />
          </LineChartPro>
        </Box>
      </Stack>
    </>
  );
};

export default SearchImpressionShare;
